/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useEffect, useState } from "react";

import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";
import { Asset, DetailedUser, Effect, Nullable, Path, Setter, User } from "../../utils/Interfaces";

import { Box, Stack, CircularProgress, Grid } from "@mui/material";
import AssigneesList from "../office-assignments/AssigneesList";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import UserAssetsPanel from "./UserAssetsPanel";
import { selectExistingUsers } from "../admin/user-management/userManagementSlice";
import { refreshUsers } from "../admin/user-management/userManagementFunctions";
import AssignAssetModal from "./AssignAssetModal";
import { sortUsersByName } from "../../utils/Functions";
import ModifyAssetModal from "./ModifyAssetModal";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

const AssetsManager = () => {
    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const [loading, setLoading] = useState<boolean>(false);

    const assignees: DetailedUser[] = useAppSelector(selectExistingUsers);
    const [selectedUser, setSelectedUser] = useState<Nullable<DetailedUser>>(null);
    const [assetAssignModalOpen, setAssetAssignModalOpen] = useState<boolean>(false);

    // The following state is put here to preserve optimal modal placement within the DOM. This function
    // receives events from child elements and opens the relevant modal as required.
    const [assetModifyModalOpen, setAssetModifyModalOpen] = useState<boolean>(false);
    const [selectedAsset, setSelectedAsset] = useState<Nullable<Asset>>(null);
    function openModifyModalForAsset(asset: Asset) {
        setSelectedAsset(asset);
        setAssetModifyModalOpen(true);
    }

    useEffect(() => {
        refreshUsers(dispatch, setLoading);
    }, [dispatch]);

    useEffect(() => {}, [selectedUser]);

    if (!selectedUser && assignees.length > 0) {
        setSelectedUser(sortUsersByName(assignees)[0] as DetailedUser);
    }

    let breadcrumbPaths: Path[] = [
        { text: "Techwise Portal", route: "/" },
        { text: "Admin", route: "/?section=admin" },
        { text: "Assign Assets", route: "/assets/assign" },
    ];

    if (loading) {
        return (
            <Stack direction="row" justifyContent="center" width="100%" sx={{ mt: 10 }}>
                <Stack direction="column" justifyContent="center">
                    <CircularProgress size={60} />
                </Stack>
            </Stack>
        );
    }

    return (
        <Box>
            <StyledBreadcrumbs paths={breadcrumbPaths} />

            <AssignAssetModal
                open={assetAssignModalOpen}
                handleClose={() => setAssetAssignModalOpen(false)}
                user={selectedUser}
                rowsPerPage={20}
                // Make a deep copy of the user to trigger a re-render on this component's lists.
                onAssetAssignedToUser={(action: Effect) => {
                    setSelectedUser({ ...selectedUser } as DetailedUser);
                    action();
                }}
            />

            {/*Only load render the modal is the selected asset is rendered.*/}
            {selectedAsset && (
                <ModifyAssetModal
                    open={assetModifyModalOpen}
                    handleClose={() => setAssetModifyModalOpen(false)}
                    asset={selectedAsset as Asset}
                    // Make a deep copy of the user to trigger a re-render on this component's lists.
                    onAssetModified={() => setSelectedUser({ ...selectedUser } as DetailedUser)}
                />
            )}

            <Grid direction="row" container>
                <Grid xs={3} item>
                    <AssigneesList
                        assignees={assignees}
                        setSelectedUser={setSelectedUser as Setter<User>}
                        subtextFunction={(user: User) => {
                            return "Click to view assets...";
                        }}
                    />
                </Grid>
                {selectedUser !== null && (
                    <Grid xs={9} item>
                        <UserAssetsPanel
                            onOpenAssetAssignModal={() => setAssetAssignModalOpen(true)}
                            onOpenModifyAssetModal={openModifyModalForAsset}
                            user={selectedUser}
                        />
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default AssetsManager;
